import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SMS } from "@/constants/testIds";
import { getSmsSandboxStatus, sendSmsSandboxTest } from "@/lib/smsSandboxApi";

const DEFAULT_DESTINATION = "+14254147755";
const DEFAULT_MESSAGE = "EMRbyMD SMS sandbox test. This is a test message.";

export default function SmsSandboxTestPage() {
  const [destination, setDestination] = useState(DEFAULT_DESTINATION);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSmsSandboxStatus()
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        if (data?.defaultDestination) setDestination(data.defaultDestination);
        if (data?.defaultMessage) setMessage(data.defaultMessage);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Unable to load SMS sandbox status.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const send = async (event) => {
    event.preventDefault();
    setSending(true);
    setError("");
    setResult(null);
    try {
      const data = await sendSmsSandboxTest({
        destinationPhoneNumber: destination.trim(),
        message: message.trim(),
      });
      setResult(data);
      toast.success("Test SMS accepted by AWS.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text = typeof detail === "string" ? detail : "AWS SMS send failed.";
      setError(text);
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={SMS.page}>
      <div>
        <h1 className="text-page-title font-semibold text-slate-900" data-testid={SMS.title}>
          SMS Sandbox Test
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Administrator-only AWS End User Messaging SMS sandbox check. This is not
          patient messaging. Do not send PHI. Use the AWS US simulator destination
          {" "}{DEFAULT_DESTINATION}.
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-teal-50 p-2 text-teal-700">
            <MessageSquare className="h-4 w-4" />
          </div>
          <div className="space-y-1 text-sm text-slate-600">
            <p>
              Configuration set:{" "}
              <span className="font-medium text-slate-900" data-testid={SMS.configSet}>
                {status?.configurationSet || "EMRbyMD-SANDBOX"}
              </span>
            </p>
            <p>
              Phone pool / origination identity:{" "}
              <span className="font-medium text-slate-900" data-testid={SMS.origination}>
                {status?.originationIdentity || "EMRbyMD-SANDBOX"}
              </span>
            </p>
            <p>Region: {status?.region || "us-east-1"}</p>
            <p>
              AWS credentials:{" "}
              {status?.credentialsAvailable ? "detected" : "not detected on this host"}
            </p>
          </div>
        </div>
      </section>

      <form
        onSubmit={send}
        className="space-y-4 rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
      >
        <div className="space-y-2">
          <Label htmlFor={SMS.destination}>Destination phone number</Label>
          <Input
            id={SMS.destination}
            data-testid={SMS.destination}
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
            placeholder="+14254147755"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={SMS.message}>Message</Label>
          <Textarea
            id={SMS.message}
            data-testid={SMS.message}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={4}
          />
        </div>
        <Button type="submit" data-testid={SMS.send} disabled={sending}>
          {sending ? "Sending…" : "Send Test SMS"}
        </Button>
      </form>

      {error ? (
        <div
          data-testid={SMS.result}
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
        </div>
      ) : null}

      {result ? (
        <div
          data-testid={SMS.result}
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
        >
          <p className="font-medium">AWS accepted the test SMS.</p>
          <p className="mt-1">
            Message ID:{" "}
            <span data-testid={SMS.messageId}>{result.messageId || "not returned"}</span>
          </p>
          <p className="mt-1">Destination: {result.destination}</p>
          <p className="mt-1">Configuration set: {result.configurationSet}</p>
        </div>
      ) : null}
    </div>
  );
}
